import React, {useEffect, useState, FC, useCallback, memo, useContext, useReducer, useMemo} from 'react';
import {Link, navigate} from 'gatsby';
import {Location} from '@reach/router';
import {faSignOutAlt} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import {UserContext} from '../../../contexts/user-context';

import {
  getRawCookie,
  removeNewUser,
  removeUserEmail,
  removeUserHash,
  removeUserName,
  removeUserToken,
  sendUserDataToExtension,
} from '../../../helpers';
import Menu from './components/menu';
import {HeaderState} from './types';

import logo from '../../../images/logo.svg';
import background1 from '../../../images//header/ltd-85/background-1.svg';
import background2 from '../../../images//header/ltd-85/background-2.svg';
import promoText from '../../../images//header/ltd-85/promo-text.svg';
import mobileBanner from '../../../images//header/ltd-85/banner-mobile.svg';

import './styles.scss';
import {useMutation, useQuery} from '@apollo/client';
import {LOG_OUT} from '../../../graphql/mutations';
import {planIcon} from '../../../images/svgIcons';
import Counter from '../../countdown';

const Header: FC<{refetchUser: any; loading: boolean}> = ({refetchUser, loading}) => {
  const {user, hexofyUser, setUser} = useContext(UserContext);
  const [showPromo, setShowPromo] = useState(false);
  const [hexofyPlanName, setHexofyPlanName] = useState('');

  const [log_out] = useMutation(LOG_OUT);

  const logOut = async (): Promise<void> => {
    const {data} = await log_out({variables: {id: user?.id, device_key: getRawCookie('device_key')}});
    if (data && data.UserOps && data.UserOps.logout && !data.UserOps.logout.error) {
      removeUserToken();
      removeUserHash();
      removeUserEmail();
      removeUserName();
      removeNewUser();
      sendUserDataToExtension();
      setUser(null);
      refetchUser();
    }
  };

  useEffect(() => {
    if (hexofyUser?.hfUser?.pricing_package) {
      const plan =
        hexofyUser?.hfUser?.pricing_package === 'FREE'
          ? 'Free'
          : hexofyUser?.hfUser?.pricing_package === 'STARTER'
          ? `Starter`
          : hexofyUser?.hfUser?.pricing_package === 'UNLIMITED'
          ? 'Unlimited'
          : hexofyUser?.hfUser?.pricing_package === 'TEAM'
          ? `Team ${hexofyUser?.hfUser?.pricing_package_count || 1}X`
          : 'N/A';

      setHexofyPlanName(plan);
      // setShowPromo(hexofyUser?.hfUser?.pricing_package_interval !== 'LTD' && hexofyUser?.hfUser?.pricing_package && hexofyUser?.hfUser?.pricing_package !== 'FREE' ? false : true);
    } else {
      setHexofyPlanName('');
      // hexofyUser && setShowPromo(true);
    }
  }, [hexofyUser]);

  const [isGravatarOpen, setIsGravatarOpen] = useReducer((val: boolean) => !val, false);
  const [state, changeState] = useState<HeaderState>({
    isMenuCollapsed: true,
    navBarFixClassName: 'header_area',
    isButtonClicked: false,
    isDropDownOpened: false,
  });
  useEffect(() => {
    window.addEventListener('scroll', handleScrollEvent);
    return () => {
      window.removeEventListener('scroll', handleScrollEvent);
    };
  }, [state.navBarFixClassName]);

  const handleScrollEvent = () => {
    const top: number = document.documentElement.scrollTop || document.body.scrollTop;
    //150 instead of 1 for promos
    if (top > 150 && state.navBarFixClassName !== 'header_area navbar_fixed') {
      changeState({
        ...state,
        navBarFixClassName: 'header_area navbar_fixed',
      });
    } else if (top <= 1 && state.navBarFixClassName === 'header_area navbar_fixed') {
      changeState({
        ...state,
        navBarFixClassName: 'header_area',
      });
    }
  };

  const toggleHandler = useCallback((): void => {
    changeState({
      ...state,
      isMenuCollapsed: !state.isMenuCollapsed,
      isDropDownOpened: !state.isDropDownOpened,
    });
  }, [state]);

  const gravatarSrc = useMemo(() => {
    return user && user.email ? 'https://hexofy.com/avatar-hexofy.svg' : '';
  }, [user]);

  // const handleRedirect = useCallback(() => {
  //   window.location.href = returnDashUrl() + 'profile';
  // }, []);
  // setFreshChatUserData(user);

  // useEffect(() => {
  //   if (!global?.window?.location?.pathname.includes('pricing')) {
  //     setShowPromo(true);
  //   } else {
  //     setShowPromo(false);
  //   }
  // }, []);

  //!---------------------------------------------
  return (
    <header className={`header white_bg ${state.navBarFixClassName}`}>
      {showPromo ? (
        <>
          <div
            className="w-100 position-relative d-none d-lg-flex promo-header"
            style={{background: '#FFC500', cursor: 'pointer', height: '68px'}}
            onClick={() => navigate('/#pricing')}
          >
            <img src={background1} alt="" className="illustration1" />
            <div className="d-flex position-relative m-auto" style={{maxWidth: '1000px'}}>
              <img src={promoText} alt="" style={{maxWidth: '100%', maxHeight: '68px', zIndex: 100}} />
              <Counter date="Dec 11 2023 07:00:00 GMT" title="LIMITED TIME OFFER" />
            </div>
            <img src={background2} alt="" className="illustration2 illustration-xl-none" />
          </div>
          <div
            className="w-100 position-relative d-flex d-lg-none promo-header"
            style={{background: '#FFC500', cursor: 'pointer'}}
            onClick={() => navigate('/#pricing')}
          >
            <img src={mobileBanner} alt="Promo" className="m-auto" style={{maxWidth: '250px', maxHeight: '180px'}} />
            <div className="m-auto counter_absolute">
              <Counter date="Dec 11 2023 07:00:00 GMT" title="LIMITED TIME OFFER" />
            </div>
          </div>
        </>
      ) : null}
      <nav className="navbar navbar-expand-md menu_one menu_five">
        <div className="container">
          <Link to="/" className="navbar-brand sticky_logo">
            <img src={logo} alt="Hexofy.com" loading="eager" />
          </Link>
          <button
            className={classNames('navbar-toggler d-block d-md-none mr-4', {
              collapsed: state.isMenuCollapsed,
            })}
            onClick={toggleHandler}
            type="button"
            data-testid="button"
            data-toggle="collapse"
            data-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="menu_toggle">
              <span className="hamburger">
                <span />
                <span />
                <span />
              </span>
              <span className="hamburger-cross">
                <span />
                <span />
              </span>
            </span>
          </button>
          <div
            className={classNames('collapse navbar-collapse', {
              show: !state.isMenuCollapsed,
            })}
            id="navbarSupportedContent"
            data-testid="navbar-show"
          >
            <Location>
              {({location}) => (
                <ul className="navbar-nav menu w_menu pl_50">
                  <Menu
                    pathName={location.pathname}
                    gravatarSrc={gravatarSrc}
                    refectUserData={() => {
                      refetchUser();
                      setUser(null);
                    }}
                  />
                </ul>
              )}
            </Location>
          </div>
          <div
            className={`d-flex ${state.isDropDownOpened ? 'collapsed-plan-name' : ''} ${
              loading || !user ? 'pulse-placeholder' : ''
            }`}
          >
            {hexofyPlanName && user && user.email && (
              <div
                className={state.isMenuCollapsed ? 'd-none d-sm-block plan-name' : 'plan-name'}
                onClick={() => navigate('/profile/my-plans/')}
              >
                {planIcon} {hexofyPlanName}
              </div>
            )}
            {!state.isDropDownOpened && user && user.email ? (
              <div
                className={classNames('btn-group user-details m-auto', {
                  show: isGravatarOpen,
                })}
              >
                <button
                  type="button"
                  onClick={setIsGravatarOpen}
                  className="logged-btn is-dropdown"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  {/* <span className="mr-3 text-truncate" style={{maxWidth: '150px'}}>
                        {(user && user.firstName)}
                      </span> */}
                  <img
                    alt={(user && user.firstName) || ''}
                    src={user?.photo || gravatarSrc}
                    className="rounded-circle"
                    loading="eager"
                  />
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="logged-btn not-dropdown"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <span className="m_r_5 my-auto text-truncate" style={{maxWidth: '150px'}}>
                    {user && user.firstName}
                  </span>
                  <img
                    alt={(user && user.firstName) || ''}
                    src={user?.photo || gravatarSrc}
                    className="rounded-circle"
                    loading="eager"
                  />
                </button>
                {isGravatarOpen && (
                  <button className="dropdown-back" type="button" onClick={() => navigate('/profile')} />
                )}
                <div className="is-dropdown">
                  <div
                    className={classNames('dropdown-menu dropdown-menu-right', {
                      show: isGravatarOpen,
                    })}
                  >
                    {/* <li>
                        <a href={returnDashUrl()} className="dropdown-item m-0 p-0">
                          <FontAwesomeIcon icon={faChartLine} className="user-icon" />
                          Dashboard
                        </a>
                      </li> */}
                    <li>
                      <a className="dropdown-item m-0 p-0" onClick={logOut}>
                        <FontAwesomeIcon icon={faSignOutAlt} className="user-icon" />
                        Sign out
                      </a>
                    </li>
                  </div>
                </div>
                <div className="not-dropdown">
                  {/* <div style={{margin: '0 10px'}}>
                      <a href={returnDashUrl()} className="dropdown-item p-0">
                        <FontAwesomeIcon icon={faChartLine} className="user-icon" />
                        Dashboard
                      </a>
                    </div> */}
                  <div className="m_l_10">
                    <a className="dropdown-item p-0" onClick={logOut}>
                      <FontAwesomeIcon icon={faSignOutAlt} className="user-icon" />
                      Sign out
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {loading || !user ? (
                  <>
                    <div className="line-placeholder line-placeholder-long" />
                    <div className="line-placeholder line-placeholder-long" />
                    <div className="line-placeholder line-placeholder-long" />
                  </>
                ) : (
                  !loading &&
                  !user?.id &&
                  !state.isDropDownOpened && (
                    <div className="d-flex">
                      <Link className="software_banner_btn d-md-block header_registration_btn" to="/#registration">
                        Get started
                      </Link>
                      <Link className="login_btn my-auto d-none d-sm-block" to="/#login" state={{goBack: true}}>
                        Login
                      </Link>
                      {/* <Link
                        className="software_banner_btn d-none d-md-block header_registration_btn outline ml-3"
                        to="/#demo"
                      >
                        Request a demo
                      </Link> */}
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default memo(Header);
